import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { AtRiskReport, getAtRiskProjects } from '../Api/Projects/projectsApi';
import { accountScopedRead } from './accountScopedRead';

const subject = new BehaviorSubject<AtRiskReport | null>(null);
const loadingSubject = new BehaviorSubject<boolean>(false);

export const getAtRiskProjectsSubject = (): BehaviorSubject<AtRiskReport | null> => subject;
export const getAtRiskProjectsLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

// A failed load (e.g. 404 while the backend flag is off) just hides the section — no global banner.
const reads = accountScopedRead({ subject, request: getAtRiskProjects, empty: null, toValue: (data) => data });

export const refreshAtRiskReport = reads.refresh;

export interface UseAtRiskProjectsReturn {
  atRiskReport: AtRiskReport | null;
  loading: boolean;
  fetchAtRiskReport: (forceRefresh?: boolean) => Promise<void>;
  clearAtRiskReport: () => void;
}

export const useAtRiskProjects = (): UseAtRiskProjectsReturn => {
  const [atRiskReport, setAtRiskReport] = useState<AtRiskReport | null>(subject.getValue());
  const [loading, setLoading] = useState<boolean>(loadingSubject.getValue());

  useEffect(() => {
    const reportSub = subject.subscribe(setAtRiskReport);
    const loadingSub = loadingSubject.subscribe(setLoading);
    return () => {
      reportSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  const fetchAtRiskReport = async (forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue()) return;
    if (subject.getValue() && !forceRefresh) return;

    loadingSubject.next(true);
    try {
      await reads.load();
    } finally {
      loadingSubject.next(false);
    }
  };

  const clearAtRiskReport = () => subject.next(null);

  return { atRiskReport, loading, fetchAtRiskReport, clearAtRiskReport };
};
